<template>
  <!--
    Visibility is driven by `visible` (parent-controlled). The parent
    flips it on when the first agent activity appears AND keeps it on
    until the user explicitly dismisses the panel via the ✕ button
    here — so once the user has seen their agents, the panel doesn't
    keep flashing in and out as individual chips come and go.
  -->
  <transition name="agents-panel">
    <aside v-if="visible" class="agents-panel">
      <div class="agents-panel__header">
        <div class="agents-panel__brand">
          <AtlasLogo :size="18" variant="brand" />
          <h2 class="agents-panel__title">
            <span class="agents-panel__title-accent">Atlas</span>
            <span class="agents-panel__title-main">Agents</span>
          </h2>
        </div>
        <button
          type="button"
          class="agents-panel__close"
          aria-label="Close agents panel"
          title="Close panel"
          @click="$emit('close')"
        >
          ×
        </button>
      </div>

      <div class="agents-panel__list">
        <!--
          Empty state: panel is kept visible after the user has
          dismissed every chip, so they can leave it parked. Tells
          them the agents are on standby rather than showing a blank
          rail that might read as broken.
        -->
        <div
          v-if="activeChains.length === 0 && activeDocCreations.length === 0 && readyToCreate.length === 0"
          class="agents-panel__empty"
        >
          <p class="agents-panel__empty-title">On standby</p>
          <p class="agents-panel__empty-body">
            Your agents are ready. They'll show up here when a task kicks off.
          </p>
        </div>

        <!-- ── Prompt Chain Agent (indigo — "think") ─────────── -->
        <div v-if="activeChains.length > 0" class="agent-card">
          <div class="agent-card__row">
            <div
              class="agent-card__avatar"
              :class="{ 'agent-card__avatar--working': chainAgentWorking }"
            >
              <AtlasLogo :size="22" variant="brand" />
            </div>
            <div class="agent-card__meta">
              <div class="agent-card__name">Prompt Chain Agent</div>
              <div class="agent-card__status-row">
                <span
                  class="agent-card__dot"
                  :class="{ 'agent-card__dot--working': chainAgentWorking }"
                />
                <span
                  class="agent-card__status"
                  :class="chainAgentWorking ? 'agent-card__status--working' : 'agent-card__status--idle'"
                >
                  {{ chainAgentWorking ? 'Working' : 'Idle' }}
                </span>
                <span class="agent-card__count">
                  {{ activeChains.length }} task{{ activeChains.length === 1 ? '' : 's' }}
                </span>
              </div>
            </div>
          </div>

          <div class="agent-card__chains">
            <ChainProgressStrip
              v-for="chain in activeChains"
              :key="chain.execution_id"
              :progress="chain"
              @jump-to="(id) => $emit('jump-to', id)"
              @dismiss="(execId) => $emit('dismiss-chain', execId)"
            />
          </div>
        </div>

        <!--
          ── Document Creation Agent (emerald — "build") ───────
          Appears once the first doc-creation request is kicked off,
          OR when a chain output arrives that's document-formatter
          shaped but hasn't been run yet (a "Queued" entry). Card
          stays pinned after terminal until the user dismisses the
          strip, same UX as the chain card.
        -->
        <div v-if="activeDocCreations.length > 0 || readyToCreate.length > 0" class="agent-card agent-card--doc">
          <div class="agent-card__row">
            <div
              class="agent-card__avatar"
              :class="{ 'agent-card__avatar--doc-working': docAgentWorking }"
            >
              <AtlasLogo :size="22" variant="brand" />
            </div>
            <div class="agent-card__meta">
              <div class="agent-card__name">Document Creation Agent</div>
              <div class="agent-card__status-row">
                <span
                  class="agent-card__dot"
                  :class="{ 'agent-card__dot--working': docAgentWorking }"
                />
                <span
                  class="agent-card__status"
                  :class="docAgentWorking ? 'agent-card__status--doc-working' : 'agent-card__status--idle'"
                >
                  {{ docAgentWorking ? 'Building' : 'Idle' }}
                </span>
                <span class="agent-card__count">
                  <template v-if="readyToCreate.length > 0 && activeDocCreations.length === 0">
                    {{ readyToCreate.length }} queued
                  </template>
                  <template v-else-if="readyToCreate.length > 0">
                    {{ activeDocCreations.length }} active · {{ readyToCreate.length }} queued
                  </template>
                  <template v-else>
                    {{ activeDocCreations.length }} doc{{ activeDocCreations.length === 1 ? '' : 's' }}
                  </template>
                </span>
              </div>
            </div>
          </div>

          <!--
            Queued strips sit above the live progress list so the
            "Run next" affordance is always at the top of the card.
            Each row is a chain output that's document-formatter-shaped
            but hasn't been kicked off yet. Run uses the same REST
            shortcut as the chat bubble CTA — both stay in sync via
            the shared `readyToCreate` map's `kickedOff` flag.
          -->
          <div v-if="readyToCreate.length > 0" class="agent-card__queued">
            <div
              v-for="entry in readyToCreate"
              :key="entry.execution_id"
              class="queued-row"
            >
              <div class="queued-row__top">
                <v-icon size="14" color="#059669">mdi-clock-outline</v-icon>
                <span class="queued-row__name">
                  {{ entry.chain_name || `Execution #${entry.execution_id}` }}
                </span>
                <span class="queued-row__label">Queued</span>
              </div>
              <div class="queued-row__actions">
                <button
                  v-if="entry.narrationMessageId"
                  type="button"
                  class="queued-row__view"
                  title="Scroll to the chain output"
                  @click="$emit('jump-to', entry.narrationMessageId)"
                >
                  <v-icon size="12">mdi-arrow-down</v-icon>
                  View output
                </button>
                <span class="queued-row__right">
                  <button
                    type="button"
                    class="queued-row__run"
                    :disabled="entry.kickedOff"
                    @click="$emit('run-ready', entry.execution_id)"
                  >
                    <v-icon v-if="entry.kickedOff" size="12" class="queued-row__spin">mdi-loading</v-icon>
                    <v-icon v-else size="12">mdi-play</v-icon>
                    {{ entry.kickedOff ? 'Starting…' : 'Run' }}
                  </button>
                  <button
                    type="button"
                    class="queued-row__dismiss"
                    title="Dismiss — don't create this document"
                    @click="$emit('dismiss-ready', entry.execution_id)"
                  >
                    Dismiss
                  </button>
                </span>
              </div>
            </div>
          </div>

          <div v-if="activeDocCreations.length > 0" class="agent-card__chains">
            <DocCreationProgressStrip
              v-for="doc in activeDocCreations"
              :key="doc.doc_creation_request_id"
              :progress="doc"
              @jump-to="(id) => $emit('jump-to', id)"
              @dismiss="(reqId) => $emit('dismiss-doc-creation', reqId)"
            />
          </div>
        </div>
      </div>
    </aside>
  </transition>
</template>

<script>
import { computed, defineComponent } from 'vue';
import AtlasLogo from './AtlasLogo.vue';
import ChainProgressStrip from './ChainProgressStrip.vue';
import DocCreationProgressStrip from './DocCreationProgressStrip.vue';

export default defineComponent({
  name: 'AgentsPanel',
  components: { AtlasLogo, ChainProgressStrip, DocCreationProgressStrip },
  props: {
    /**
     * Parent-controlled visibility. Set true by the parent as soon as
     * any agent activity first appears in the session; stays true
     * until the user clicks the panel's ✕ — the sticky behaviour stops
     * the panel flashing in/out as individual chips come and go.
     */
    visible: { type: Boolean, default: false },
    /**
     * Live chain-progress entries (sorted array) from the shared
     * `agentChat` composable. Drives the Prompt Chain Agent card.
     */
    activeChains: { type: Array, default: () => [] },
    /**
     * Live doc-creation-progress entries (sorted array). Drives the
     * second card — the Document Creation Agent.
     */
    activeDocCreations: { type: Array, default: () => [] },
    /**
     * Chain outputs that are document-formatter-shaped but haven't
     * had their creation kicked off yet. Rendered as "Queued" strips
     * under the Document Creation Agent card with Run + Dismiss
     * controls, so users can fire them from the panel instead of
     * scrolling the chat to find the bubble CTA.
     */
    readyToCreate: { type: Array, default: () => [] },
  },
  // `dismiss-chain` / `dismiss-doc-creation` / `run-ready` /
  // `dismiss-ready` are distinct so the parent can route each to the
  // correct composable method without inspecting the id shape.
  // `close` dismisses the whole panel.
  emits: ['jump-to', 'dismiss-chain', 'dismiss-doc-creation', 'run-ready', 'dismiss-ready', 'close'],
  setup(props) {
    // Per-agent "is anything actively running" — flips the card header
    // from "Working"/"Building" to "Idle" when every chip inside has
    // reached a terminal state. Previously the labels were hardcoded so
    // the agent looked busy long after everything had finished.
    const CHAIN_RUNNING = new Set(['queued', 'running']);
    const DOC_RUNNING = new Set(['Queued', 'Processing', 'Extracting']);

    const chainAgentWorking = computed(() =>
      props.activeChains.some((c) => CHAIN_RUNNING.has(c.status)),
    );
    const docAgentWorking = computed(() =>
      props.activeDocCreations.some((d) => DOC_RUNNING.has(d.status)),
    );

    return { chainAgentWorking, docAgentWorking };
  },
});
</script>

<style scoped>
.agents-panel {
  flex-shrink: 0;
  /* Fill the overlay wrapper's width; the parent (.panel-overlay--right)
     is the single source of truth for sizing so the aside can't overflow
     past the viewport's right edge. */
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  /*
   * Re-enable pointer events on the panel itself. The parent overlay
   * wrapper in vAgentChat.vue is intentionally `pointer-events: none`
   * so empty gutter space doesn't intercept clicks meant for things
   * underneath (e.g. body-portalled v-menu dropdowns). The panel
   * surface is the only thing in that gutter that should be clickable.
   */
  pointer-events: auto;
  /* No border / no solid background: panel blends into the chat surface
     so it reads as the same canvas, distinguished only by its content. */
}
.agents-panel__header {
  flex-shrink: 0;
  padding: 24px 20px 12px 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.agents-panel__close {
  background: transparent;
  border: 0;
  padding: 2px 6px;
  font-size: 16px;
  line-height: 1;
  color: rgb(156 163 175);
  cursor: pointer;
  transition: color 0.15s ease;
}
.agents-panel__close:hover {
  color: rgb(55 65 81);
}

.agents-panel__empty {
  margin-top: 12px;
  padding: 18px 14px;
  border: 1px dashed rgb(209 213 219);
  border-radius: 12px;
  text-align: center;
}
.agents-panel__empty-title {
  margin: 0;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgb(107 114 128);
}
.agents-panel__empty-body {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: rgb(107 114 128);
}
.agents-panel__brand {
  display: flex;
  align-items: center;
  gap: 8px;
}
.agents-panel__title {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.agents-panel__title-accent {
  color: rgb(79 70 229);
}
.agents-panel__title-main {
  color: rgb(55 65 81);
  letter-spacing: 0.1em;
}

.agents-panel__list {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.agent-card {
  border-radius: 12px;
  border: 1px solid rgba(199, 210, 254, 0.7);
  background: linear-gradient(to bottom, rgba(238, 242, 255, 0.6) 0%, rgba(255, 255, 255, 0.4) 100%);
  padding: 12px;
  box-shadow: 0 1px 2px rgba(99, 102, 241, 0.06);
}
/* Emerald palette variant — Document Creation Agent. */
.agent-card--doc {
  border-color: rgba(110, 231, 183, 0.7);
  background: linear-gradient(to bottom, rgba(209, 250, 229, 0.6) 0%, rgba(255, 255, 255, 0.4) 100%);
  box-shadow: 0 1px 2px rgba(16, 185, 129, 0.08);
}
.agent-card__row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.agent-card__avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  /* Neutral idle ring; the `--working` / `--doc-working` modifiers
     override with their coloured ring + pulse. */
  box-shadow: 0 0 0 1px rgb(209 213 219);
}
.agent-card__avatar--working {
  box-shadow: 0 0 0 1px rgb(165 180 252);
  animation: agent-avatar-pulse 2.4s ease-in-out infinite;
}
@keyframes agent-avatar-pulse {
  0%, 100% {
    box-shadow: 0 0 0 1px rgb(165 180 252), 0 0 0 0 rgba(99, 102, 241, 0.35);
  }
  50% {
    box-shadow: 0 0 0 1px rgb(165 180 252), 0 0 0 6px rgba(99, 102, 241, 0);
  }
}
/* Emerald avatar pulse for the Document Creation Agent. */
.agent-card__avatar--doc-working {
  box-shadow: 0 0 0 1px rgb(110 231 183);
  animation: doc-agent-avatar-pulse 2.4s ease-in-out infinite;
}
@keyframes doc-agent-avatar-pulse {
  0%, 100% {
    box-shadow: 0 0 0 1px rgb(110 231 183), 0 0 0 0 rgba(16, 185, 129, 0.35);
  }
  50% {
    box-shadow: 0 0 0 1px rgb(110 231 183), 0 0 0 6px rgba(16, 185, 129, 0);
  }
}
.agent-card__status--doc-working {
  color: rgb(6 95 70);
}

.agent-card__meta {
  min-width: 0;
  flex: 1;
}
.agent-card__name {
  font-size: 13px;
  font-weight: 600;
  color: rgb(17 24 39);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.agent-card__status-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}
.agent-card__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  /* Idle colour; `--working` overrides to emerald + pulses. */
  background: rgb(156 163 175);
  flex-shrink: 0;
}
.agent-card__dot--working {
  background: rgb(16 185 129);
  animation: agent-dot-pulse 1.4s ease-in-out infinite;
}
@keyframes agent-dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.6; transform: scale(0.85); }
}
.agent-card__status {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgb(75 85 99);
}
.agent-card__status--working {
  color: rgb(67 56 202);
}
.agent-card__status--idle {
  color: rgb(107 114 128);
}
.agent-card__count {
  margin-left: auto;
  font-size: 10px;
  color: rgb(107 114 128);
}
.agent-card__chains {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Queued strips: rows of document-formatter-shaped chain outputs the
   user hasn't run yet. Sit between the card header and the live
   progress list so the Run affordance is always visible first. */
.agent-card__queued {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.queued-row {
  border: 1px solid rgb(167 243 208);
  background: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  padding: 8px 10px;
}
.queued-row__top {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.queued-row__name {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  font-weight: 500;
  color: rgb(31 41 55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.queued-row__label {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgb(4 120 87);
}
.queued-row__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.queued-row__right {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.queued-row__view {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 0;
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 11px;
  color: rgb(107 114 128);
  cursor: pointer;
  transition: color 0.15s ease;
}
.queued-row__view:hover {
  color: rgb(17 24 39);
}
.queued-row__run {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgb(5 150 105);
  color: #ffffff;
  border: 0;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}
.queued-row__run:hover:not(:disabled) {
  background: rgb(4 120 87);
}
.queued-row__run:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.queued-row__spin {
  animation: queued-row-spin 1s linear infinite;
}
@keyframes queued-row-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.queued-row__dismiss {
  background: transparent;
  border: 0;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  color: rgb(107 114 128);
  cursor: pointer;
  transition: color 0.15s ease;
}
.queued-row__dismiss:hover {
  color: rgb(17 24 39);
}

/* Slide + fade in from the right when activity starts; reverse on exit. */
.agents-panel-enter-active,
.agents-panel-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.agents-panel-enter,
.agents-panel-enter-from,
.agents-panel-leave-to {
  opacity: 0;
  transform: translateX(12px);
}

/* Collapse on narrow screens so mobile chat stays readable. */
@media (max-width: 1024px) {
  .agents-panel {
    display: none;
  }
}
</style>
